import { useRef } from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { Flex, FlexProps, Input, Text, InputGroup } from '@chakra-ui/react';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useTranslation } from 'react-i18next';

type ControlProps<T extends FieldValues> = Pick<UseControllerProps<T>, 'control' | 'name' | 'rules' | 'disabled'>;

type ReceiptUploadProps<T extends FieldValues> = ControlProps<T> & {
  name: string;
  accept?: string;
  onFileChange?: (file: File | null) => void;
  error?: string;
  wrapperProps?: FlexProps;
};

export function ReceiptUpload<T extends FieldValues>(props: ReceiptUploadProps<T>) {
  const { name, accept, control, onFileChange, error, wrapperProps } = props;
  const { t } = useTranslation();

  const innerRef = useRef<HTMLInputElement>(null);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, name } }) => (
        <Flex alignItems='center' position='relative' width='100%' gap={6}>
          <InputGroup width='100%' display='flex' flexDirection='column' gap={2}>
            <Input
              name={name}
              type='file'
              display='none'
              ref={innerRef}
              accept={accept}
              onChange={event => {
                const file = event?.target?.files?.[0];

                onChange?.(file ?? null);
                onFileChange?.(file ?? null);
              }}
            />
            <Flex gap={2} onClick={() => innerRef?.current?.click()} cursor='pointer' {...wrapperProps}>
              <ChakraFeatherIcon name='upload' size='16px' color='brand.500' />
              <Text
                title={value?.name ?? t('pages.fundraising_campaign.components.receipt_upload.receipt_upload_placeholder')}
                color={value?.name ? 'text.main' : 'brand.500'}
                textDecoration='underline'
                noOfLines={1}
              >
                {value?.name ?? t('pages.fundraising_campaign.components.receipt_upload.receipt_upload_placeholder')}
              </Text>
            </Flex>

            {error ? (
              <Text fontSize='14px' color='red.500'>
                {error}
              </Text>
            ) : null}
          </InputGroup>
        </Flex>
      )}
    />
  );
}
